// ============================================================================
// useOffline Hook — IndexedDB caching + offline queue + sync
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';

const DB_NAME = 'firmassist_offline';
const DB_VERSION = 1;
const CACHE_STORE = 'cache';
const QUEUE_STORE = 'syncQueue';

// ---- IndexedDB helpers ----
const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(CACHE_STORE)) {
      db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
    }
    if (!db.objectStoreNames.contains(QUEUE_STORE)) {
      const store = db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
      store.createIndex('createdAt', 'createdAt');
    }
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const dbOperation = async (storeName, mode, operation) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const result = operation(store);
      tx.oncomplete = () => resolve(result?.result || true);
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('IndexedDB error:', e);
    return null;
  }
};

// ---- Cache API ----
export const cacheData = async (key, data, ttlMinutes = 60) => {
  return dbOperation(CACHE_STORE, 'readwrite', (store) => {
    store.put({
      key,
      data,
      cachedAt: Date.now(),
      expiresAt: Date.now() + (ttlMinutes * 60 * 1000)
    });
  });
};

export const getCachedData = async (key) => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return resolve(null);
        if (Date.now() > record.expiresAt) {
          // Expired — delete and return null (but if offline, still return stale data)
          if (navigator.onLine) {
            dbOperation(CACHE_STORE, 'readwrite', (s) => s.delete(key));
            return resolve(null);
          }
          // Offline: return stale data with flag
          return resolve({ ...record.data, _stale: true, _cachedAt: record.cachedAt });
        }
        resolve(record.data);
      };
      req.onerror = () => resolve(null);
    });
  } catch (e) { return null; }
};

export const clearCache = async () => {
  return dbOperation(CACHE_STORE, 'readwrite', (store) => store.clear());
};

// Session & auth-state handling (2026-10-06): firm-scoped entries are keyed by
// uid AND firmId, so a cache written for Firm X can never be read under Firm Y
// (the old keys were uid-only). The cache store only ever holds firm data and is
// cleared as a whole on logout / firm move / removal / suspension / firm loss.
export const offlineCacheKey = (name, uid, firmId) => `${name}::${uid}::${firmId}`;

export const clearOfflineQueue = async () => {
  return dbOperation(QUEUE_STORE, 'readwrite', (store) => store.clear());
};

// ---- Sync Queue API ----
export const queueAction = async (action) => {
  return dbOperation(QUEUE_STORE, 'readwrite', (store) => {
    store.add({
      ...action,
      createdAt: Date.now(),
      status: 'pending'
    });
  });
};

const getQueuedActions = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(QUEUE_STORE, 'readonly');
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result?.filter(a => a.status === 'pending') || []);
      req.onerror = () => resolve([]);
    });
  } catch (e) { return []; }
};

const removeFromQueue = async (id) => {
  return dbOperation(QUEUE_STORE, 'readwrite', (store) => store.delete(id));
};

// ---- Offline-enabled fetch wrapper ----
export const offlineFetch = async (cacheKey, fetchFn, ttlMinutes = 30) => {
  // Try network first
  if (navigator.onLine) {
    try {
      const result = await fetchFn();
      // Cache the successful result
      if (result && (result.success !== false)) {
        await cacheData(cacheKey, result, ttlMinutes);
      }
      return result;
    } catch (e) {
      // Network failed — fall back to cache
      const cached = await getCachedData(cacheKey);
      if (cached) return cached;
      throw e;
    }
  }
  // Offline — use cache
  const cached = await getCachedData(cacheKey);
  if (cached) return cached;
  return { success: false, error: 'You are offline and no cached data is available.' };
};

// ---- The Hook ----
const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const syncInProgress = useRef(false);

  // Monitor online status
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      // Auto-sync when back online
      syncQueue();
    };
    const goOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // Initial check
    if (!navigator.onLine) {
      setShowBanner(true);
    }

    // Check queue count periodically
    const interval = setInterval(async () => {
      const actions = await getQueuedActions();
      setQueueCount(actions.length);
    }, 5000);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(interval);
    };
  }, []);

  // Show banner briefly when coming back online with queued items
  useEffect(() => {
    if (isOnline && queueCount > 0 && !showBanner) {
      setShowBanner(true);
    }
    if (isOnline && queueCount === 0 && showBanner) {
      const timer = setTimeout(() => setShowBanner(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, queueCount]);

  // Sync queued actions
  const syncQueue = useCallback(async () => {
    if (syncInProgress.current || !navigator.onLine) return;
    syncInProgress.current = true;
    setIsSyncing(true);

    try {
      const actions = await getQueuedActions();
      let synced = 0;
      let failed = 0;

      for (const action of actions) {
        try {
          // Execute the queued action
          if (action.type === 'firestore_write') {
            const { collection: collName, data, operation } = action;
            // Dynamic import to avoid circular deps
            const { collection, addDoc, updateDoc, deleteDoc, doc } = await import('firebase/firestore');
            const { db } = await import('../firebase-config');

            if (operation === 'add') {
              await addDoc(collection(db, collName), data);
            } else if (operation === 'update' && action.docId) {
              await updateDoc(doc(db, collName, action.docId), data);
            } else if (operation === 'delete' && action.docId) {
              await deleteDoc(doc(db, collName, action.docId));
            }
          } else if (action.type === 'api_call') {
            // Generic API call replay
            const fn = action.fn;
            if (typeof fn === 'function') await fn();
          }

          await removeFromQueue(action.id);
          synced++;
        } catch (e) {
          console.warn('Sync failed for action:', action.id, e);
          failed++;
        }
      }

      const remaining = await getQueuedActions();
      setQueueCount(remaining.length);

      if (synced > 0) {
        console.log(`Offline sync: ${synced} synced, ${failed} failed`);
      }
    } catch (e) {
      console.error('Sync queue error:', e);
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
    }
  }, []);

  const dismissBanner = useCallback(() => setShowBanner(false), []);

  return {
    isOnline,
    queueCount,
    isSyncing,
    showBanner,
    sync: syncQueue,
    dismissBanner,
    // Expose utilities
    cacheData,
    getCachedData,
    clearCache,
    queueAction,
    offlineFetch
  };
};

export default useOffline;
